import React, { useState, useEffect } from 'react'

// components
import { useTranslation } from 'react-i18next'

import { Grid, Button, useMediaQuery, useTheme } from '@mui/material'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useConfirmationDialogProvider } from '../../context/ConfirmationDialogContext'
import { useDataApi } from '../../context/DataApiContext'
import useStyles from './styles'

// eslint-disable-next-line max-len
import CreateConstructorDialog from '../../components/Constructors/components/CreateConstructorDialog'
// eslint-disable-next-line max-len
import ModifyConstructorDialog from '../../components/Constructors/components/ModifyConstructorDialog'
import Table from '../../components/Table'
import generateColumns from './columns'

export default function ConstructorsList() {
  const { t } = useTranslation()

  const { showConfirmationDialog } = useConfirmationDialogProvider()
  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableState, setTableState] = useState({
    page: 0,
    sort: null,
    filters: null,
  })
  const [tableData, setTableData] = useState(null)
  const [openCreateConstructorDialog, setOpenCreateConstructorDialog] =
    useState(false)
  const [openModifyConstructorDialog, setOpenModifyConstructorDialog] =
    useState(false)
  const [constructorToModify, setConstructorToModify] = useState(false)

  const refreshData = (keepPage = false) => {
    if (keepPage) {
      setTableState({ page: tableState.page, sort: null, filters: null })
    } else {
      setTableState({ page: 0, sort: null, filters: null })
    }
  }

  const resource = 'constructors'

  useEffect(() => {
    dataProvider.constructor
      .getList(
        resource,
        {
          range: { page: tableState.page + 1 },
          filters: tableState.filters,
          sort: tableState.sort,
        },
        false
      )
      .then((response) => {
        setItems(response.data.items)
        setTableData(response.data)
        setLoading(false)
      })
      .catch(() => showNotification('error', t('errors.api-error')))
    // eslint-disable-next-line
  }, [tableState])

  const onModify = (rowIndex) => {
    setConstructorToModify(tableData.items[rowIndex])
    setOpenModifyConstructorDialog(true)
  }

  const onDelete = (rowIndex) => {
    const { id, name } = tableData.items[rowIndex]
    showConfirmationDialog(
      t('constructors-list.confirm-constructor-deletion', {
        name,
      }),
      () => {
        dataProvider
          .delete(`constructors`, { id })
          .then(() => {
            refreshData()
            showNotification('success', t('constructors-list.delete-success'))
          })
          .catch(() => showNotification('error', t('errors.api-error')))
      },
      t('common.delete')
    )
  }

  const tableColumns = generateColumns({
    t,
    classes,
    items,
    onModify,
    onDelete,
  })

  if (loading) {
    return <Loading />
  }
  return (
    <>
      <CreateConstructorDialog
        open={openCreateConstructorDialog}
        onClose={() => setOpenCreateConstructorDialog(false)}
        onCreated={() => {
          setOpenCreateConstructorDialog(false)
          refreshData()
        }}
      />
      <ModifyConstructorDialog
        open={openModifyConstructorDialog}
        onClose={() => {
          setOpenModifyConstructorDialog(false)
          setConstructorToModify({})
        }}
        onModified={() => {
          refreshData(true)
          setOpenModifyConstructorDialog(false)
        }}
        constructorToModify={constructorToModify}
      />
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={4}>
          <PageTitle title={t('constructors-list.title')} />
        </Grid>
        <Grid item container sm={8} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            fullWidth={fullWidth}
            onClick={() => setOpenCreateConstructorDialog(true)}
          >
            {t('constructors-list.add-constructor')}
          </Button>
        </Grid>
      </Grid>
      <Grid item className={classes.tableContainer}>
        <Table
          id={resource}
          columns={tableColumns}
          data={tableData}
          page={tableState.page}
          sort={tableState.sort}
          onChangePage={(currentPage) => {
            setTableState({ ...tableState, page: currentPage })
          }}
          onColumnSortChange={(changedColumn, direction) => {
            const newSort = {
              field: changedColumn,
              direction: direction.toUpperCase(),
            }
            setTableState({ ...tableState, sort: newSort })
          }}
        />
      </Grid>
    </>
  )
}
